import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWRMutation from 'swr/mutation';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import {
    useHeadToHeadEnvironments,
    useHeadToHeadOptions,
    useHeadToHeadResults,
} from '/@/store/head-to-head/apiProviders';
import { getBrands } from '/@/store/public-data/selectors';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance } from '/@/store/session/selectors';
import { formatEnvironmentRange } from '/@/utils';
import { getMaxAndMinEnvironmentFromRankings } from '/@/views/dashboard/rv/components/product-catalog';
import { useCatalogProductById } from '../catalog';
import useDebounce from '../debounce/useDebounce';

const processBrands = (tableData) => {
    const uniqueBrandsIds = [...new Set(tableData.map(({ brand }) => brand.id))];

    return uniqueBrandsIds
        .map((brandId) => {
            const row = tableData.find((row) => row.brand.id === brandId).brand;
            return { label: row.name, value: row.id };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
};

const processTable = (tableData, trialsData = undefined) => {
    return tableData.map((row) => ({
        linearRegressionEnabled: trialsData && Object.hasOwn(trialsData, row.productId),
        value: row.difference,
        trialCount: row.comparisons,
        winRate: row.winRate,
        result: row.result,
        other: {
            name: row.name,
            id: row.productId,
            brand: row.brand,
        },
    }));
};
const processTrials = (tableData, trialsData, head, other) => {
    const otherData = tableData.find((row) => row.other.id === other.id);
    let trialsByProduct;
    if (trialsData && trialsData[other.id]) {
        trialsByProduct = {
            [other.id]: trialsData[other.id].points.map((trial, index) => ({
                trialKey: `${index}`,
                environment: trial.environment,
                result: trial.oResult,
            })),
            [head.id]: trialsData[other.id].points.map((trial, index) => ({
                trialKey: `${index}`,
                environment: trial.environment,
                result: trial.hResult,
            })),
        };
    }

    return { ...otherData, head, trialsByProduct };
};

export const useH2H = (
    { crop, brand, zoneIds, altitude, productId },
    { enabled }: { enabled?: boolean } = { enabled: true }
) => {
    const units = useUnitProvider();

    const brands = useSelector(getBrands);
    const internalBrandsIds = settings.internalBrands;
    const internalBrandsCodenames = [
        ...new Set(
            internalBrandsIds
                .map((brandId) => brands.find((b) => b.id === brandId))
                // `brands.find` can return undefined (an internal-brand id not in `brands`), and the
                // anchor `brand` can be undefined while the product is still loading or on an instance
                // with no brand for this crop - guard both so the H2H hook doesn't crash the page.
                .filter((b) => b && b.id !== brand?.id && b.countryId === brand?.countryId && b.type == brand?.type)
                .map((b) => b.codename)
                .filter(Boolean)
        ),
    ];

    const [zone, setZone] = useState();
    const [head, setHead] = useState();
    const [environment, setEnvironment] = useState();
    const [yieldBrands, setYieldBrands] = useState();
    const [humidityBrands, setHumidityBrands] = useState();
    const [otherForLinearRegression, setOtherForLinearRegression] = useState();
    const params = useParams();
    const productProfileId = Number(params.productId);

    const h2hOptions = useHeadToHeadOptions(
        {
            crop,
            zoneIds,
            brandCodename: brand?.codename,
            productId,
        },
        { enabled }
    );
    const environments = useHeadToHeadEnvironments({
        crop,
        zoneId: zone?.id,
        headId: head?.id,
        altitude,
        brandsCodenames: internalBrandsCodenames,
    });
    const results = useHeadToHeadResults({
        crop,
        zoneId: zone?.id,
        headId: head?.id,
        minEnv: environment?.min,
        maxEnv: environment?.max,
        altitude,
        brandsCodenames: internalBrandsCodenames,
    });

    const zoneOptions = useMemo(() => {
        if (h2hOptions.data) return h2hOptions.data.map((z) => ({ label: _(z.shortName), value: z }));

        return [];
    }, [h2hOptions.data]);

    const headOptions = useMemo(() => {
        if (zone?.products)
            return zone.products
                .map((z) => ({ label: z.name, value: z }))
                .sort((a, b) => a.label.localeCompare(b.label));
        return [];
    }, [zone]);

    const yieldBrandOptions = useMemo(() => {
        if (results.data?.yieldTable) {
            return processBrands(results.data.yieldTable);
        }
        return [];
    }, [results.data]);

    const humidityBrandOptions = useMemo(() => {
        if (results.data?.humidityTable) {
            return processBrands(results.data.humidityTable);
        }
        return [];
    }, [results.data]);

    const humidityTable = useMemo(() => {
        if (results.data?.humidityTable) return processTable(results.data.humidityTable);
        return [];
    }, [results.data]);

    const filteredHumidityTable = useMemo(
        () =>
            humidityTable
                .filter((row) => humidityBrands.includes(row.other.brand.id))
                .sort((a, b) => a.other.name.localeCompare(b.other.name)),
        [humidityTable, humidityBrands]
    );

    const yieldTable = useMemo(() => {
        if (results.data?.yieldTable && head)
            return processTable(results.data.yieldTable, results.data.yieldTrials, head);
        return [];
    }, [results.data, head]);

    const filteredYieldTable = useMemo(
        () =>
            yieldTable
                .filter((row) => yieldBrands.includes(row.other.brand.id))
                .sort(
                    (a, b) =>
                        a.other.brand.name.localeCompare(b.other.brand.name) || a.other.name.localeCompare(b.other.name)
                ),
        [yieldTable, yieldBrands]
    );

    const yieldTrials = useMemo(() => {
        if (yieldTable.length > 0 && head && otherForLinearRegression && results.data)
            return processTrials(yieldTable, results.data.yieldTrials, head, otherForLinearRegression);
    }, [yieldTable, head, otherForLinearRegression, results.data]);

    const others = useMemo(() => {
        if (results.data?.yieldTrials)
            return Object.entries(results.data.yieldTrials)
                .filter(([_, row]) => yieldBrands.includes(row.brand.id))
                .map(([rowId, row]) => ({
                    id: Number(rowId),
                    name: row.name,
                    brand: row.brand,
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        return [];
    }, [results.data, yieldBrands]);

    useEffect(() => {
        if (h2hOptions.data && h2hOptions.data.length > 0) setZone(h2hOptions.data[0]);
        else setZone(undefined);
    }, [h2hOptions.data]);

    useEffect(() => {
        if (zone && zone.products.length > 0) setHead(zone.products[0]);
        else setHead(undefined);
    }, [zone]);

    useEffect(() => {
        if (others.length) {
            setOtherForLinearRegression(others[0]);
        } else setOtherForLinearRegression(undefined);
    }, [others]);

    useEffect(() => {
        setYieldBrands(yieldBrandOptions.map(({ value }) => value));
    }, [yieldBrandOptions]);

    useEffect(() => {
        setHumidityBrands(humidityBrandOptions.map(({ value }) => value));
    }, [humidityBrandOptions]);

    const product = useCatalogProductById({ id: productProfileId, crop: 'corn' });

    const headPositioning = getMaxAndMinEnvironmentFromRankings(
        product.data?.rankings || [],
        product.data?.newRankings || [],
        units
    );

    useEffect(() => {
        if (environments.data) {
            if (environments.data.type === 'slider') {
                setEnvironment({
                    min: Math.round(environments.data.minEnvironment / 100) * 100,
                    max: Math.round(environments.data.maxEnvironment / 100) * 100,
                });
            } else if (environments.data.type === 'options') {
                const options = environments.data.options;
                const matchingOption =
                    options.find(
                        ([min, max]) =>
                            min === headPositioning?.baseMinEnvironment && max === headPositioning?.baseMaxEnvironment
                    ) ??
                    options.find(([min]) => min === headPositioning?.baseMinEnvironment) ??
                    options.find(([, max]) => max === headPositioning?.baseMaxEnvironment) ??
                    options[0];
                setEnvironment({
                    min: matchingOption?.[0],
                    max: matchingOption?.[1],
                });
            }
        }
    }, [environments.data, headPositioning?.minEnvironment?.value, headPositioning?.maxEnvironment?.value]);

    const environmentOptions = useMemo(
        () =>
            (environments.data?.options || []).map((environment) => ({
                value: { min: environment[0], max: environment[1] },
                label: formatEnvironmentRange({ min: environment[0], max: environment[1] }, (value) =>
                    units.toYield(value, 'corn', 2)
                ),
            })),
        [environments.data]
    );

    // Hack to void two calls when whant to no debounce
    const [environmentUI, setEnvironmentUI] = useState();
    const debouncedEnvironmentUI = useDebounce(environmentUI, 300);
    useEffect(() => {
        const handleSetEnvironment = (newEnv) => {
            if (newEnv === environment) return;

            setEnvironment(newEnv);
            setOtherForLinearRegression(undefined);
        };
        handleSetEnvironment(debouncedEnvironmentUI);
    }, [debouncedEnvironmentUI]);
    useEffect(() => {
        setEnvironmentUI(environment);
    }, [environment]);

    const selectedEnvironmentOptionValue = useMemo(() => {
        return environmentOptions.find((e) => {
            return e.value.min === environment?.min && e.value.max === environment?.max;
        })?.value;
    }, [environmentOptions, environment]);

    return {
        zone,
        setZone: (newZone) => {
            if (newZone === zone) return;

            setZone(newZone);
            setHead(undefined);
            setEnvironment(undefined);
            setOtherForLinearRegression(undefined);
        },

        head,
        setHead: (newHead) => {
            if (newHead === head) return;

            setHead(newHead);
            setEnvironment(undefined);
            setOtherForLinearRegression(undefined);
        },

        environment: environmentUI,
        setEnvironment: setEnvironmentUI,
        setEnvironmentInstanteneous: setEnvironment,
        environmentOptions,
        selectedEnvironmentOptionValue,

        yieldBrands,
        setYieldBrands,

        humidityBrands,
        setHumidityBrands,

        otherForLinearRegression,
        setOtherForLinearRegression,

        h2hOptions,
        environments: environments.data,
        results: results.data,

        zoneOptions,
        headOptions,
        yieldBrandOptions,
        humidityBrandOptions,
        others,
        yieldTable,
        humidityTable,
        yieldTrials,
        filteredHumidityTable,
        filteredYieldTable,

        isValidating: {
            environment: environments.isValidating,
            h2hOptions: h2hOptions.isValidating,
            results: results.isValidating,
        },
        errors: {
            environment: environments.errors,
            h2hOptions: h2hOptions.errors,
            results: results.errors,
        },
    };
};

export const createH2HLogFromAPI = async ({
    cornHead,
    soyHead,
    appInstance,
}: {
    cornHead?: number;
    soyHead?: number;
    appInstance: number;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/ui/h2h-log/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString(), {
        corn_head: cornHead,
        soy_head: soyHead,
        appInstance: appInstance,
    });
    return res.data;
};

export const useH2HLogCreateMutation = () => {
    const appInstance = useSelector(getAppInstance).id;
    return useSWRMutation(
        { key: 'useH2HLogCreateMutation', appInstance },
        ({ appInstance }, { arg: { cornHead, soyHead } }: { arg: { cornHead?: number; soyHead?: number } }) =>
            createH2HLogFromAPI({ cornHead, soyHead, appInstance })
    );
};
